<script lang="ts">
  /**
   * One-time import of the old timesheets.
   *
   * Deliberately takes a paste or a CSV file rather than reading Drive directly.
   * Reading an existing spreadsheet would need the Google Picker, which needs a
   * live access token in the browser — and this app keeps tokens server-side.
   * For something run twice, ever, "select all, copy, paste" is the better
   * trade. It also works for sheets owned by the client rather than by you.
   */
  import { store } from '$lib/store.svelte';
  import { api } from '$lib/api';
  import { parseLegacySheet, toEntryInputs, type DateOrder, type ParseResult } from '$lib/legacy';

  let clientId = $state(store.activeClientId ?? '');
  let text = $state('');
  let forcedOrder = $state<DateOrder | undefined>(undefined);
  let importing = $state(false);
  let imported = $state<number | null>(null);

  const result = $derived.by((): ParseResult | null =>
    text.trim() ? parseLegacySheet(text, forcedOrder) : null,
  );

  const warnings = $derived(result?.rows.filter((r) => r.warning) ?? []);

  async function onFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) text = await file.text();
  }

  async function runImport() {
    if (!result || !clientId) return;
    importing = true;
    imported = null;
    try {
      const res = await api.importEntries(clientId, toEntryInputs(result.rows, clientId));
      imported = res.imported;
      text = '';
      await store.sync();
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    } finally {
      importing = false;
    }
  }
</script>

<div class="stack">
  <section class="card stack">
    <h2>Import an old timesheet</h2>
    <ol class="muted fine steps">
      <li>Open your old sheet in Google Sheets.</li>
      <li>Select the data (including the header row) and copy.</li>
      <li>Paste it below — or upload a CSV export.</li>
    </ol>

    <div>
      <label for="i-client">Import into</label>
      <select id="i-client" bind:value={clientId}>
        <option value="" disabled>Pick a client</option>
        {#each store.clients as c (c.id)}
          <option value={c.id}>{c.name}</option>
        {/each}
      </select>
    </div>

    <div>
      <label for="i-paste">Paste rows</label>
      <textarea
        id="i-paste"
        bind:value={text}
        rows="6"
        placeholder={'Date\tHours\tNotes\n2/9/2026\t6\tIntro, onboarding'}
      ></textarea>
    </div>

    <div class="row">
      <input type="file" accept=".csv,.tsv,text/csv,text/plain" onchange={onFile} />
    </div>
  </section>

  {#if result}
    <section class="card stack">
      <div class="spread">
        <h2>Preview</h2>
        <span class="muted fine">
          {result.rows.length} to import · {result.skipped.length} skipped
        </span>
      </div>

      {#if result.ambiguous}
        <div class="warn">
          <strong>Ambiguous dates.</strong> Nothing in this file distinguishes day-first from
          month-first — every date has both parts at 12 or below. Pick the right one:
          <div class="row" style="margin-top:.5rem">
            <button
              class:primary={forcedOrder === 'dmy'}
              onclick={() => (forcedOrder = 'dmy')}
            >
              Day first (31/12/2026)
            </button>
            <button
              class:primary={forcedOrder === 'mdy'}
              onclick={() => (forcedOrder = 'mdy')}
            >
              Month first (12/31/2026)
            </button>
          </div>
        </div>
      {:else}
        <p class="muted fine">
          Detected <strong>
            {result.dateOrder === 'iso'
              ? 'ISO dates'
              : result.dateOrder === 'dmy'
                ? 'day-first dates'
                : 'month-first dates'}
          </strong>.
          <button class="ghost link" onclick={() => (forcedOrder = result.dateOrder === 'dmy' ? 'mdy' : 'dmy')}>
            Switch
          </button>
        </p>
      {/if}

      {#if warnings.length}
        <div class="warn">
          <strong>{warnings.length} assumption{warnings.length > 1 ? 's' : ''} made:</strong>
          <ul>
            {#each warnings.slice(0, 5) as row (row.date + row.notes)}
              <li>{row.warning}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if result.skipped.length}
        <details>
          <summary>{result.skipped.length} rows skipped</summary>
          <ul class="skipped">
            {#each result.skipped.slice(0, 20) as s (s.line)}
              <li><code>line {s.line}</code> {s.reason} — <span class="muted">{s.raw}</span></li>
            {/each}
          </ul>
        </details>
      {/if}

      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>Date</th><th class="num">Hours</th><th>Notes</th></tr>
          </thead>
          <tbody>
            {#each result.rows.slice(0, 12) as row (row.date + row.notes + row.hours)}
              <tr class:warned={!!row.warning}>
                <td>{row.date}</td>
                <td class="num">{row.hours}</td>
                <td class="muted">{row.notes}</td>
              </tr>
            {/each}
          </tbody>
        </table>
        {#if result.rows.length > 12}
          <p class="muted fine">…and {result.rows.length - 12} more.</p>
        {/if}
      </div>

      <button
        class="primary"
        onclick={runImport}
        disabled={importing || !clientId || !result.rows.length}
      >
        {importing ? 'Importing…' : `Import ${result.rows.length} entries`}
      </button>

      {#if imported !== null}
        <p class="ok">Imported {imported} entries ✓</p>
      {/if}
    </section>
  {/if}
</div>

<style>
  .steps {
    margin: 0;
    padding-left: 1.1rem;
  }

  .steps li {
    margin: 0.15rem 0;
  }

  .fine {
    font-size: 0.82rem;
  }

  textarea {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.82rem;
    resize: vertical;
  }

  .warn {
    background: color-mix(in srgb, #ffd43b 12%, var(--surface));
    border: 1px solid color-mix(in srgb, #ffd43b 35%, var(--line));
    border-radius: var(--radius);
    padding: 0.65rem 0.85rem;
    font-size: 0.86rem;
  }

  .warn ul {
    margin: 0.35rem 0 0;
    padding-left: 1.1rem;
  }

  .link {
    min-height: auto;
    padding: 0 0.2rem;
    color: var(--accent);
    text-decoration: underline;
    font-size: inherit;
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.88rem;
  }

  th,
  td {
    text-align: left;
    padding: 0.35rem 0.55rem;
    border-bottom: 1px solid var(--line);
  }

  tr.warned td {
    background: color-mix(in srgb, #ffd43b 8%, transparent);
  }

  .skipped {
    font-size: 0.8rem;
    padding-left: 1.1rem;
  }

  .skipped code {
    color: var(--muted);
  }

  details summary {
    cursor: pointer;
    font-size: 0.85rem;
    color: var(--muted);
  }

  .ok {
    color: var(--ok);
    font-weight: 650;
  }
</style>
